import React, { forwardRef, useState } from 'react';
import {
  PDF_LAYOUT_MAX_EXTRAS,
  PDF_LAYOUT_MAX_BODY_SECTIONS,
  PDF_LAYOUT_MAX_TABLE_COLUMNS,
  DEFAULT_BODY_SECTIONS,
  DEFAULT_TABLE_COLUMNS,
  type PdfBodySection,
  type PdfBodySectionKind,
  type PdfCustomLayout,
  type PdfTableColumn,
  type PdfTableColumnKey,
  type LogoAlign,
  logoXFracFromAlign,
  sanitizeHex
} from '../../../pdf/pdfCustomLayout';
import type {
  LayoutPatch,
  OverrideFieldPath,
  PdfDocType,
  PdfStudioBranding
} from '../../../pdf/pdfStudioState';
import { hasOverrideField } from '../../../pdf/pdfStudioState';
import type { StudioSectionId } from './PdfStudioPage';
import {
  AccordionSection,
  ColorField,
  SelectField,
  SliderField,
  TextAreaField,
  TextField,
  ToggleField
} from './pdf-studio-ui';

export type PdfStudioControlsProps = {
  docType: PdfDocType;
  activeSection: StudioSectionId;
  branding: PdfStudioBranding;
  globalLayout: PdfCustomLayout;
  overridePatch: LayoutPatch;
  legacyLocked: boolean;
  onBrandingChange: (patch: Partial<PdfStudioBranding>) => void;
  onPatchGlobal: (patch: LayoutPatch) => void;
  onPatchOverride: (patch: LayoutPatch) => void;
  onResetOverride: (path: OverrideFieldPath) => void;
  onLogoAlign: (align: LogoAlign) => void;
};

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

type PdfTemplateValue = 'generic' | 'premium' | 'zera';

const PDF_TEMPLATE_GENERIC = {
  value: 'generic',
  label: 'Generic — neutral, minimal chrome'
} as const;
const PDF_TEMPLATE_PREMIUM = {
  value: 'premium',
  label: 'Premium — full layout controls'
} as const;
const PDF_TEMPLATE_ZERA = {
  value: 'zera',
  label: 'Classic premium — bundled CFAA/ACMO badges'
} as const;

const PdfStudioControls = forwardRef<HTMLInputElement, PdfStudioControlsProps>(function PdfStudioControls(
  props,
  logoInputRef
) {
  const {
    docType,
    activeSection,
    branding,
    globalLayout,
    overridePatch,
    legacyLocked,
    onBrandingChange,
    onPatchGlobal,
    onPatchOverride,
    onResetOverride,
    onLogoAlign
  } = props;

  const docLabel =
    docType === 'quote' ? 'Quote' : docType === 'invoice' ? 'Invoice' : 'Inspection';

  /**
   * The "Classic premium" (zera) layout is a tenant-licensed template. Only expose it
   * if this workspace already has it selected or has the legacy raster-export flag on.
   * Every other workspace just sees Generic / Premium.
   */
  const zeraTemplateAvailable =
    branding.pdfTemplate === 'zera' || branding.useClientRasterExport === true;
  const pdfTemplateOptions: ReadonlyArray<{ value: PdfTemplateValue; label: string }> =
    zeraTemplateAvailable
      ? [PDF_TEMPLATE_GENERIC, PDF_TEMPLATE_PREMIUM, PDF_TEMPLATE_ZERA]
      : [PDF_TEMPLATE_GENERIC, PDF_TEMPLATE_PREMIUM];

  const safePdfTemplate: PdfTemplateValue =
    branding.pdfTemplate === 'premium' ||
    (branding.pdfTemplate === 'zera' && zeraTemplateAvailable)
      ? branding.pdfTemplate
      : 'generic';

  /**
   * Helper for override controls — reads the override value if set, otherwise the global value,
   * and reports whether the override is active.
   */
  function ov<T>(path: OverrideFieldPath, globalValue: T): { value: T; has: boolean } {
    const has = hasOverrideField(overridePatch, path);
    if (!has) return { value: globalValue, has: false };
    const [section, field] = path.split('.') as [string, string];
    const sub = (overridePatch as Record<string, unknown>)[section] as Record<string, unknown> | undefined;
    const v = sub ? (sub[field] as T) : globalValue;
    return { value: v ?? globalValue, has: true };
  }

  return (
    <aside className="pdf-studio-right">
      <div className="pdf-studio-controls">
        {/* =============================================================== */}
        {/* DOCUMENT INFO — branding-level fields shown across all PDFs      */}
        {/* =============================================================== */}
        {activeSection === 'section-info' ? (
        <AccordionSection id="section-info" title="Document Info & Branding" variant="info" defaultOpen>
          <TextField
            label="Company name"
            scope="global"
            value={branding.companyName || ''}
            placeholder="Acme Fire Protection Ltd."
            onChange={(companyName) => onBrandingChange({ companyName })}
          />
          <TextAreaField
            label="Address"
            scope="global"
            rows={2}
            value={branding.address || ''}
            placeholder="123 Industrial Way, Suite 4&#10;Toronto, ON M5V 3A8"
            onChange={(address) => onBrandingChange({ address })}
          />
          <TextField
            label="Phone"
            scope="global"
            type="tel"
            value={branding.phone || ''}
            placeholder="(416) 555-0199"
            onChange={(phone) => onBrandingChange({ phone })}
          />
          <TextField
            label="Email"
            scope="global"
            type="email"
            value={branding.email || ''}
            placeholder="hello@acmefire.ca"
            onChange={(email) => onBrandingChange({ email })}
          />
          <TextField
            label="Website"
            scope="global"
            type="url"
            value={branding.website || ''}
            placeholder="www.acmefire.ca"
            onChange={(website) => onBrandingChange({ website })}
          />
          <TextField
            label="HST / GST number"
            scope="global"
            value={branding.hstNumber || ''}
            placeholder="123456789RT0001"
            onChange={(hstNumber) => onBrandingChange({ hstNumber })}
          />
          <ColorField
            label="Brand color (accent fallback)"
            scope="global"
            disabled={legacyLocked}
            value={branding.brandColor || '#2d8dcc'}
            onChange={(brandColor) => onBrandingChange({ brandColor })}
          />
          <SelectField
            label="PDF template"
            scope="global"
            disabled={legacyLocked}
            value={safePdfTemplate}
            options={pdfTemplateOptions}
            onChange={(pdfTemplate) => onBrandingChange({ pdfTemplate })}
          />
          <p className="pdf-studio-hint">
            The Studio preview always shows premium chrome so every control is visible while you
            edit. Choose <strong>Premium</strong> here for the same appearance on downloaded PDFs.
          </p>
        </AccordionSection>
        ) : null}

        {/* =============================================================== */}
        {/* HEADER                                                           */}
        {/* =============================================================== */}
        {activeSection === 'section-header' ? (
        <AccordionSection id="section-header" title="Header" variant="header" defaultOpen>
          <ColorField
            label="Background color"
            value={globalLayout.colors.headerBg}
            scope="global"
            disabled={legacyLocked}
            onChange={(headerBg) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, headerBg } })
            }
          />
          <label className="pdf-studio-field">
            <div className="pdf-studio-field-label-row">
              <span>Logo position</span>
              <span className="pdf-studio-scope pdf-studio-scope--global">Global</span>
            </div>
            <select
              disabled={legacyLocked}
              value={globalLayout.logo.align || 'left'}
              onChange={(e) => onLogoAlign(e.target.value as LogoAlign)}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>
          <SliderField
            label="Logo width"
            scope="global"
            min={10}
            max={60}
            unit="%"
            disabled={legacyLocked}
            value={Math.round(globalLayout.logo.slotWidthFrac * 100)}
            onChange={(pct) => {
              const slotWidthFrac = pct / 100;
              const align = globalLayout.logo.align || 'left';
              onPatchGlobal({
                logo: {
                  ...globalLayout.logo,
                  slotWidthFrac,
                  xFrac: logoXFracFromAlign(align, slotWidthFrac)
                }
              });
            }}
          />
          <SliderField
            label="Logo vertical position"
            scope="global"
            min={0}
            max={60}
            unit="%"
            disabled={legacyLocked}
            value={Math.round(globalLayout.logo.yFrac * 100)}
            onChange={(pct) =>
              onPatchGlobal({ logo: { ...globalLayout.logo, yFrac: pct / 100 } })
            }
          />
          <label className="pdf-studio-field" id="field-main-logo">
            <div className="pdf-studio-field-label-row">
              <span>Main logo</span>
              <span className="pdf-studio-scope pdf-studio-scope--global">Global</span>
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                void fileToDataUrl(f).then((dataUrl) => onBrandingChange({ logoBase64: dataUrl }));
              }}
            />
            {branding.logoBase64 ? (
              <div className="pdf-studio-row">
                <img className="pdf-studio-preview-thumb" src={branding.logoBase64} alt="" />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => onBrandingChange({ logoBase64: '' })}
                >
                  Remove
                </button>
              </div>
            ) : null}
          </label>
          <ToggleField
            label="Show CFAA badge"
            scope="global"
            disabled={legacyLocked}
            checked={globalLayout.badges.showCfaa !== false}
            onChange={(showCfaa) =>
              onPatchGlobal({ badges: { ...globalLayout.badges, showCfaa } })
            }
          />
          <label className="pdf-studio-field">
            <span>CFAA logo upload</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                void fileToDataUrl(f).then((dataUrl) => onBrandingChange({ cfaaLogoBase64: dataUrl }));
              }}
            />
            {branding.cfaaLogoBase64 ? (
              <div className="pdf-studio-row">
                <img className="pdf-studio-preview-thumb" src={branding.cfaaLogoBase64} alt="" />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => onBrandingChange({ cfaaLogoBase64: '' })}
                >
                  Remove
                </button>
              </div>
            ) : null}
          </label>
          <ToggleField
            label="Show ACMO badge"
            scope="global"
            disabled={legacyLocked}
            checked={globalLayout.badges.showAcmo !== false}
            onChange={(showAcmo) =>
              onPatchGlobal({ badges: { ...globalLayout.badges, showAcmo } })
            }
          />
          <label className="pdf-studio-field">
            <span>ACMO logo upload</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                void fileToDataUrl(f).then((dataUrl) => onBrandingChange({ acmoLogoBase64: dataUrl }));
              }}
            />
            {branding.acmoLogoBase64 ? (
              <div className="pdf-studio-row">
                <img className="pdf-studio-preview-thumb" src={branding.acmoLogoBase64} alt="" />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => onBrandingChange({ acmoLogoBase64: '' })}
                >
                  Remove
                </button>
              </div>
            ) : null}
          </label>
          <SliderField
            label="Badges right inset"
            scope="global"
            min={0}
            max={15}
            unit="%"
            disabled={legacyLocked}
            value={Math.round(globalLayout.badges.rightInsetFrac * 100)}
            onChange={(pct) =>
              onPatchGlobal({ badges: { ...globalLayout.badges, rightInsetFrac: pct / 100 } })
            }
          />
          <SliderField
            label="Badges vertical position"
            scope="global"
            min={0}
            max={80}
            unit="%"
            disabled={legacyLocked}
            value={Math.round(globalLayout.badges.yFrac * 100)}
            onChange={(pct) =>
              onPatchGlobal({ badges: { ...globalLayout.badges, yFrac: pct / 100 } })
            }
          />
          <SliderField
            label="Badges gap"
            scope="global"
            min={0}
            max={24}
            unit="px"
            disabled={legacyLocked}
            value={globalLayout.badges.gapPx}
            onChange={(gapPx) =>
              onPatchGlobal({ badges: { ...globalLayout.badges, gapPx } })
            }
          />
          <SliderField
            label="Header height"
            scope="global"
            min={96}
            max={170}
            unit="px"
            disabled={legacyLocked}
            value={globalLayout.type.headerHeightPx}
            onChange={(headerHeightPx) =>
              onPatchGlobal({ type: { ...globalLayout.type, headerHeightPx } })
            }
          />
          <SliderField
            label="Header vertical padding"
            scope="global"
            min={8}
            max={32}
            unit="px"
            disabled={legacyLocked}
            value={globalLayout.type.headerPaddingYPx}
            onChange={(headerPaddingYPx) =>
              onPatchGlobal({ type: { ...globalLayout.type, headerPaddingYPx } })
            }
          />
          <ToggleField
            label="Show accent stripe"
            scope="global"
            disabled={legacyLocked}
            checked={globalLayout.stripe?.show !== false}
            onChange={(show) => onPatchGlobal({ stripe: { show } })}
          />
          <ColorField
            label="Stripe color"
            value={globalLayout.colors.accentBorder}
            scope="global"
            disabled={legacyLocked}
            onChange={(accentBorder) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, accentBorder } })
            }
          />
          <SliderField
            label="Stripe width"
            scope="global"
            min={0}
            max={16}
            unit="px"
            disabled={legacyLocked}
            value={globalLayout.type.stripeWidthPx}
            onChange={(stripeWidthPx) =>
              onPatchGlobal({ type: { ...globalLayout.type, stripeWidthPx } })
            }
          />
          {!legacyLocked ? (
            <ExtraLogosEditor
              layout={globalLayout}
              onChange={(extras, extraOrder) => onPatchGlobal({ extras, extraOrder })}
            />
          ) : null}
        </AccordionSection>
        ) : null}

        {/* =============================================================== */}
        {/* TITLE ROW                                                        */}
        {/* =============================================================== */}
        {activeSection === 'section-title' ? (
        <AccordionSection id="section-title" title="Title Row" variant="title" defaultOpen>
          <ToggleField
            label="Company name before title"
            scope="global"
            disabled={legacyLocked}
            checked={globalLayout.invoiceHeader.companyFirst}
            onChange={(companyFirst) =>
              onPatchGlobal({
                invoiceHeader: { ...globalLayout.invoiceHeader, companyFirst }
              })
            }
          />
          <SelectField
            label="Company column alignment"
            scope="global"
            disabled={legacyLocked}
            value={globalLayout.invoiceHeader.companyTextAlign}
            options={[
              { value: 'right', label: 'Right' },
              { value: 'left', label: 'Left' }
            ]}
            onChange={(companyTextAlign) =>
              onPatchGlobal({
                invoiceHeader: { ...globalLayout.invoiceHeader, companyTextAlign }
              })
            }
          />
          <SliderField
            label="Title font size"
            scope="global"
            min={18}
            max={40}
            unit="px"
            disabled={legacyLocked}
            value={globalLayout.type.docTitlePx}
            onChange={(docTitlePx) =>
              onPatchGlobal({ type: { ...globalLayout.type, docTitlePx } })
            }
          />
          <ColorField
            label="Title color"
            value={globalLayout.colors.docTitleColor}
            scope="global"
            disabled={legacyLocked}
            onChange={(docTitleColor) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, docTitleColor } })
            }
          />
        </AccordionSection>
        ) : null}

        {/* =============================================================== */}
        {/* BODY & TABLE                                                     */}
        {/* =============================================================== */}
        {activeSection === 'section-body' ? (
        <AccordionSection id="section-body" title="Body & Table" variant="body" defaultOpen>
          <BodyPanel
            legacyLocked={legacyLocked}
            globalLayout={globalLayout}
            onPatchGlobal={onPatchGlobal}
          />
        </AccordionSection>
        ) : null}

        {/* =============================================================== */}
        {/* FOOTER                                                           */}
        {/* =============================================================== */}
        {activeSection === 'section-footer' ? (
        <AccordionSection id="section-footer" title="Footer" variant="footer" defaultOpen>
          <TextAreaField
            label="Disclaimer"
            scope="global"
            rows={3}
            value={branding.disclaimerText || ''}
            placeholder="This quote is valid for 30 days from the date issued…"
            onChange={(disclaimerText) => onBrandingChange({ disclaimerText })}
          />
          <TextAreaField
            label="Compliance / footer note"
            scope="global"
            rows={3}
            value={branding.complianceText || ''}
            placeholder="Services performed in accordance with applicable codes and standards."
            onChange={(complianceText) => onBrandingChange({ complianceText })}
          />
          <ColorField
            label="Disclaimer text color"
            scope="global"
            disabled={legacyLocked}
            value={globalLayout.footer?.noteColor ?? '#64748b'}
            onChange={(noteColor) => onPatchGlobal({ footer: { noteColor } })}
          />
          <ColorField
            label="Compliance text color"
            scope="global"
            disabled={legacyLocked}
            value={globalLayout.footer?.complianceColor ?? globalLayout.colors.accentBorder}
            onChange={(complianceColor) => onPatchGlobal({ footer: { complianceColor } })}
          />
          <ColorField
            label="Compliance border color"
            scope="global"
            disabled={legacyLocked}
            value={globalLayout.footer?.complianceBorderColor ?? globalLayout.colors.accentBorder}
            onChange={(complianceBorderColor) => onPatchGlobal({ footer: { complianceBorderColor } })}
          />
          <ColorField
            label="Compliance background"
            scope="global"
            disabled={legacyLocked}
            value={globalLayout.footer?.complianceBgColor ?? '#ffffff'}
            onChange={(complianceBgColor) => onPatchGlobal({ footer: { complianceBgColor } })}
          />
          <ToggleField
            label="Show footer logo strip"
            scope="global"
            disabled={legacyLocked}
            checked={globalLayout.footer?.showLogoStrip === true}
            onChange={(showLogoStrip) => onPatchGlobal({ footer: { showLogoStrip } })}
            hint="Repeats the main logo + CFAA + ACMO badges at the bottom of every page."
          />
        </AccordionSection>
        ) : null}

        {/* =============================================================== */}
        {/* PER-DOCUMENT OVERRIDES — mirror the global controls, optional   */}
        {/* =============================================================== */}
        {activeSection === 'section-overrides' ? (
        <AccordionSection
          id="section-overrides"
          title={`Per-Document Overrides (${docLabel})`}
          variant="overrides"
          defaultOpen
        >
          <p className="pdf-studio-hint">
            Any value set here applies only to <strong>{docLabel}</strong> PDFs and overrides the
            global value above. Use <em>Reset</em> on any field to revert to global.
          </p>

          {/* Header overrides */}
          <p className="pdf-studio-subhead">Header</p>
          {(() => {
            const o = ov('colors.headerBg', globalLayout.colors.headerBg);
            return (
              <ColorField
                label="Header background"
                value={o.value}
                scope="doc"
                hasOverride={o.has}
                onReset={() => onResetOverride('colors.headerBg')}
                disabled={legacyLocked}
                onChange={(headerBg) =>
                  onPatchOverride({
                    colors: { headerBg: sanitizeHex(headerBg, globalLayout.colors.headerBg) }
                  })
                }
              />
            );
          })()}
          {(() => {
            const o = ov('type.headerHeightPx', globalLayout.type.headerHeightPx);
            return (
              <SliderField
                label="Header height"
                scope="doc"
                min={96}
                max={170}
                unit="px"
                hasOverride={o.has}
                onReset={() => onResetOverride('type.headerHeightPx')}
                disabled={legacyLocked}
                value={o.value}
                onChange={(headerHeightPx) => onPatchOverride({ type: { headerHeightPx } })}
              />
            );
          })()}
          {(() => {
            const o = ov('colors.accentBorder', globalLayout.colors.accentBorder);
            return (
              <ColorField
                label="Stripe color"
                value={o.value}
                scope="doc"
                hasOverride={o.has}
                onReset={() => onResetOverride('colors.accentBorder')}
                disabled={legacyLocked}
                onChange={(accentBorder) =>
                  onPatchOverride({
                    colors: { accentBorder: sanitizeHex(accentBorder, globalLayout.colors.accentBorder) }
                  })
                }
              />
            );
          })()}
          {(() => {
            const o = ov('type.stripeWidthPx', globalLayout.type.stripeWidthPx);
            return (
              <SliderField
                label="Stripe width"
                scope="doc"
                min={0}
                max={16}
                unit="px"
                hasOverride={o.has}
                onReset={() => onResetOverride('type.stripeWidthPx')}
                disabled={legacyLocked}
                value={o.value}
                onChange={(stripeWidthPx) => onPatchOverride({ type: { stripeWidthPx } })}
              />
            );
          })()}
          {(() => {
            const o = ov('stripe.show', globalLayout.stripe?.show !== false);
            return (
              <ToggleField
                label="Show accent stripe"
                scope="doc"
                hasOverride={o.has}
                onReset={() => onResetOverride('stripe.show')}
                disabled={legacyLocked}
                checked={o.value}
                onChange={(show) => onPatchOverride({ stripe: { show } })}
              />
            );
          })()}

          {/* Title overrides */}
          <p className="pdf-studio-subhead">Title</p>
          {(() => {
            const o = ov('type.docTitlePx', globalLayout.type.docTitlePx);
            return (
              <SliderField
                label="Title size"
                scope="doc"
                min={18}
                max={40}
                unit="px"
                hasOverride={o.has}
                onReset={() => onResetOverride('type.docTitlePx')}
                disabled={legacyLocked}
                value={o.value}
                onChange={(docTitlePx) => onPatchOverride({ type: { docTitlePx } })}
              />
            );
          })()}
          {(() => {
            const o = ov('colors.docTitleColor', globalLayout.colors.docTitleColor);
            return (
              <ColorField
                label="Title color"
                value={o.value}
                scope="doc"
                hasOverride={o.has}
                onReset={() => onResetOverride('colors.docTitleColor')}
                disabled={legacyLocked}
                onChange={(docTitleColor) =>
                  onPatchOverride({
                    colors: { docTitleColor: sanitizeHex(docTitleColor, globalLayout.colors.docTitleColor) }
                  })
                }
              />
            );
          })()}
          {(() => {
            const o = ov('invoiceHeader.companyFirst', globalLayout.invoiceHeader.companyFirst);
            return (
              <ToggleField
                label="Company name before title"
                scope="doc"
                hasOverride={o.has}
                onReset={() => onResetOverride('invoiceHeader.companyFirst')}
                disabled={legacyLocked}
                checked={o.value}
                onChange={(companyFirst) =>
                  onPatchOverride({ invoiceHeader: { companyFirst } })
                }
              />
            );
          })()}

          {/* Body overrides */}
          <p className="pdf-studio-subhead">Body</p>
          {(() => {
            const o = ov('type.bodyPx', globalLayout.type.bodyPx);
            return (
              <SliderField
                label="Body font size"
                scope="doc"
                min={10}
                max={16}
                unit="px"
                hasOverride={o.has}
                onReset={() => onResetOverride('type.bodyPx')}
                disabled={legacyLocked}
                value={o.value}
                onChange={(bodyPx) => onPatchOverride({ type: { bodyPx } })}
              />
            );
          })()}
          {(() => {
            const o = ov('type.tablePx', globalLayout.type.tablePx);
            return (
              <SliderField
                label="Table font size"
                scope="doc"
                min={10}
                max={15}
                unit="px"
                hasOverride={o.has}
                onReset={() => onResetOverride('type.tablePx')}
                disabled={legacyLocked}
                value={o.value}
                onChange={(tablePx) => onPatchOverride({ type: { tablePx } })}
              />
            );
          })()}
          {(() => {
            const o = ov('colors.tableHeadBg', globalLayout.colors.tableHeadBg);
            return (
              <ColorField
                label="Table heading background"
                value={o.value}
                scope="doc"
                hasOverride={o.has}
                onReset={() => onResetOverride('colors.tableHeadBg')}
                disabled={legacyLocked}
                onChange={(tableHeadBg) =>
                  onPatchOverride({
                    colors: { tableHeadBg: sanitizeHex(tableHeadBg, globalLayout.colors.tableHeadBg) }
                  })
                }
              />
            );
          })()}
          {(() => {
            const o = ov('body.rowAltBg', globalLayout.body?.rowAltBg ?? '#f8fafc');
            return (
              <ColorField
                label="Alternating row color"
                value={o.value}
                scope="doc"
                hasOverride={o.has}
                onReset={() => onResetOverride('body.rowAltBg')}
                disabled={legacyLocked}
                onChange={(rowAltBg) =>
                  onPatchOverride({
                    body: { rowAltBg: sanitizeHex(rowAltBg, globalLayout.body?.rowAltBg ?? '#f8fafc') }
                  })
                }
              />
            );
          })()}
        </AccordionSection>
        ) : null}

        {/* =============================================================== */}
        {/* CUSTOM HTML LAYOUT — import existing design                      */}
        {/* =============================================================== */}
        {activeSection === 'section-custom-html' ? (
          <CustomHtmlSection
            docType={docType}
            docLabel={docLabel}
            legacyLocked={legacyLocked}
            branding={branding}
            onBrandingChange={onBrandingChange}
          />
        ) : null}
      </div>
    </aside>
  );
});

export default PdfStudioControls;

const CUSTOM_HTML_PLACEHOLDERS: ReadonlyArray<{ key: string; description: string }> = [
  { key: '{{docTitle}}', description: 'QUOTE / INVOICE / INSPECTION REPORT' },
  { key: '{{docNumber}}', description: 'Document serial / id (e.g. #1042)' },
  { key: '{{docDate}}', description: 'Issued / inspection date' },
  { key: '{{docDueDate}}', description: 'Invoice due date (invoices only)' },
  { key: '{{docValidUntil}}', description: 'Quote valid-until date (quotes only)' },
  { key: '{{companyName}}', description: 'Your company name' },
  { key: '{{companyAddress}}', description: 'Your company address' },
  { key: '{{companyPhone}}', description: 'Your company phone' },
  { key: '{{companyEmail}}', description: 'Your company email' },
  { key: '{{companyWebsite}}', description: 'Your company website' },
  { key: '{{hstNumber}}', description: 'HST / GST number' },
  { key: '{{clientName}}', description: 'Client / contact name' },
  { key: '{{clientAddress}}', description: 'Client / site address' },
  { key: '{{clientEmail}}', description: 'Client email' },
  { key: '{{clientPhone}}', description: 'Client phone' },
  { key: '{{clientBuilding}}', description: 'Client building name (invoices)' },
  { key: '{{subtotal}}', description: 'Subtotal formatted as currency' },
  { key: '{{taxLabel}}', description: 'e.g. "Tax (13%)" or "HST (13%)"' },
  { key: '{{taxAmount}}', description: 'Tax amount formatted as currency' },
  { key: '{{discountPercent}}', description: 'Discount percent (number)' },
  { key: '{{discountAmount}}', description: 'Discount amount formatted as currency' },
  { key: '{{total}}', description: 'Total formatted as currency' },
  { key: '{{logoImg}}', description: 'Pre-built <img> tag with your logo' },
  { key: '{{logoUrl}}', description: 'Logo data URL only (useful in src="…")' },
  { key: '{{cfaaLogoImg}}', description: 'Pre-built CFAA <img> tag (if uploaded)' },
  { key: '{{acmoLogoImg}}', description: 'Pre-built ACMO <img> tag (if uploaded)' },
  { key: '{{brandColor}}', description: 'Brand color hex (e.g. for borders/headings)' },
  { key: '{{itemsTable}}', description: 'Pre-built <table> of line items (with header row)' },
  { key: '{{itemsRows}}', description: 'Just the <tr>…</tr> rows (drop into your own <table>)' },
  { key: '{{itemsList}}', description: 'Pre-built <ul> of line items' },
  { key: '{{disclaimer}}', description: 'Disclaimer text from branding' },
  { key: '{{compliance}}', description: 'Compliance / footer note text' },
  { key: '{{generatedAt}}', description: 'ISO timestamp of render' }
];

const DEFAULT_CUSTOM_HTML_TEMPLATE = `<!-- Custom layout — edit freely. {{placeholders}} are substituted at render time. -->
<div style="max-width: 720px; margin: 0 auto; padding: 0 8px;">
  <header style="display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 3px solid {{brandColor}};">
    <div>
      {{logoImg}}
    </div>
    <div style="text-align: right;">
      <h1 style="font-size: 28px; margin: 0; color: {{brandColor}};">{{docTitle}}</h1>
      <div style="color: #475569; font-size: 12px;">{{docNumber}} &middot; {{docDate}}</div>
    </div>
  </header>

  <section style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 18px;">
    <div>
      <h3 style="margin: 0 0 4px; font-size: 11px; letter-spacing: 0.08em; color: #64748b; text-transform: uppercase;">From</h3>
      <strong>{{companyName}}</strong><br />
      {{companyAddress}}<br />
      {{companyPhone}} &middot; {{companyEmail}}<br />
      {{companyWebsite}}<br />
      {{hstNumber}}
    </div>
    <div>
      <h3 style="margin: 0 0 4px; font-size: 11px; letter-spacing: 0.08em; color: #64748b; text-transform: uppercase;">To</h3>
      <strong>{{clientName}}</strong><br />
      {{clientAddress}}<br />
      {{clientPhone}} &middot; {{clientEmail}}
    </div>
  </section>

  {{itemsTable}}

  <section style="margin-top: 16px; text-align: right;">
    <div>Subtotal: <strong>{{subtotal}}</strong></div>
    <div>{{taxLabel}}: <strong>{{taxAmount}}</strong></div>
    <div style="font-size: 18px; color: {{brandColor}}; margin-top: 6px;">Total: <strong>{{total}}</strong></div>
  </section>

  <footer style="margin-top: 24px; font-size: 11px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 10px;">
    {{disclaimer}}<br />
    {{compliance}}
  </footer>
</div>`;

const DEFAULT_CUSTOM_CSS_TEMPLATE = `/* Optional custom CSS — applied on top of baseline print styles. */
.custom-items-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
.custom-items-table th { background: var(--brand-bg, #f1f5f9); padding: 8px 10px; text-align: left; border-bottom: 1px solid #e2e8f0; font-size: 11px; text-transform: uppercase; color: #475569; }
.custom-items-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; font-size: 12px; vertical-align: top; }
.custom-items-table tr:nth-child(even) td { background: #f8fafc; }
`;

function CustomHtmlSection(props: {
  docType: PdfDocType;
  docLabel: string;
  legacyLocked: boolean;
  branding: PdfStudioBranding;
  onBrandingChange: (patch: Partial<PdfStudioBranding>) => void;
}): React.JSX.Element {
  const { docType, docLabel, legacyLocked, branding, onBrandingChange } = props;
  const enabled = Boolean(branding.useCustomPdf?.[docType]);
  const html = branding.customPdfHtml?.[docType] ?? '';
  const css = branding.customPdfCss?.[docType] ?? '';

  function updateField(field: 'customPdfHtml' | 'customPdfCss', value: string) {
    onBrandingChange({
      [field]: { ...(branding[field] || {}), [docType]: value }
    } as Partial<PdfStudioBranding>);
  }

  function setEnabled(next: boolean) {
    onBrandingChange({
      useCustomPdf: { ...(branding.useCustomPdf || {}), [docType]: next }
    });
  }

  async function importHtmlFile(file: File) {
    const text = await file.text();
    updateField('customPdfHtml', text);
    if (!enabled) setEnabled(true);
  }

  async function importCssFile(file: File) {
    const text = await file.text();
    updateField('customPdfCss', text);
  }

  return (
    <AccordionSection
      id="section-custom-html"
      title={`Custom HTML Layout (${docLabel})`}
      variant="custom"
      defaultOpen
    >
      <p className="pdf-studio-hint">
        Paste or upload your own HTML/CSS layout for <strong>{docLabel}</strong> PDFs. When enabled,
        this <em>replaces</em> the managed layout above. Use the placeholders below to inject your
        document data.
      </p>
      <ToggleField
        label={`Use custom HTML for ${docLabel}`}
        scope="doc"
        disabled={legacyLocked}
        checked={enabled}
        onChange={setEnabled}
        hint={
          enabled
            ? 'Custom HTML is active. The preview now renders your HTML with placeholders substituted.'
            : 'Disabled — managed layout above is in effect.'
        }
      />

      <div className="pdf-studio-row">
        <label className="btn-secondary pdf-studio-import-btn">
          Import HTML file…
          <input
            type="file"
            accept=".html,.htm,text/html,text/plain"
            hidden
            disabled={legacyLocked}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importHtmlFile(f);
              e.target.value = '';
            }}
          />
        </label>
        <label className="btn-secondary pdf-studio-import-btn">
          Import CSS file…
          <input
            type="file"
            accept=".css,text/css"
            hidden
            disabled={legacyLocked}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importCssFile(f);
              e.target.value = '';
            }}
          />
        </label>
        <button
          type="button"
          className="btn-secondary"
          disabled={legacyLocked}
          onClick={() => updateField('customPdfHtml', DEFAULT_CUSTOM_HTML_TEMPLATE)}
        >
          Insert starter HTML
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={legacyLocked}
          onClick={() => updateField('customPdfCss', DEFAULT_CUSTOM_CSS_TEMPLATE)}
        >
          Insert starter CSS
        </button>
      </div>

      <label className="pdf-studio-field">
        <div className="pdf-studio-field-label-row">
          <span>HTML body</span>
          <span className="pdf-studio-scope pdf-studio-scope--doc">This doc</span>
        </div>
        <textarea
          className="pdf-studio-code"
          rows={14}
          spellCheck={false}
          disabled={legacyLocked}
          value={html}
          placeholder="<div>Hello {{companyName}} — your invoice for {{clientName}}…</div>"
          onChange={(e) => updateField('customPdfHtml', e.target.value)}
        />
      </label>

      <label className="pdf-studio-field">
        <div className="pdf-studio-field-label-row">
          <span>CSS</span>
          <span className="pdf-studio-scope pdf-studio-scope--doc">This doc</span>
        </div>
        <textarea
          className="pdf-studio-code"
          rows={8}
          spellCheck={false}
          disabled={legacyLocked}
          value={css}
          placeholder="/* Optional CSS — applied on top of A4 print baseline. */"
          onChange={(e) => updateField('customPdfCss', e.target.value)}
        />
      </label>

      <details className="pdf-studio-placeholder-help">
        <summary>Available placeholders ({CUSTOM_HTML_PLACEHOLDERS.length})</summary>
        <ul className="pdf-studio-placeholder-list">
          {CUSTOM_HTML_PLACEHOLDERS.map((p) => (
            <li key={p.key}>
              <code>{p.key}</code> — {p.description}
            </li>
          ))}
        </ul>
        <p className="pdf-studio-hint">
          <strong>Notes:</strong> <code>&lt;script&gt;</code> tags, <code>on*</code> event
          handlers, and <code>javascript:</code> URIs are stripped for security. Place your HTML
          inside a single root element for cleanest results.
        </p>
      </details>

      {enabled && !html.trim() ? (
        <p className="pdf-studio-save-error" role="alert">
          Custom HTML is enabled but empty — falling back to managed layout in preview.
        </p>
      ) : null}
    </AccordionSection>
  );
}

type BodyPanelTab = 'table' | 'sections' | 'colors';

function BodyPanel(props: {
  legacyLocked: boolean;
  globalLayout: PdfCustomLayout;
  onPatchGlobal: (patch: LayoutPatch) => void;
}): React.JSX.Element {
  const { legacyLocked, globalLayout, onPatchGlobal } = props;
  const [tab, setTab] = useState<BodyPanelTab>('table');

  return (
    <div className="pdf-studio-body-panel">
      <div className="pdf-studio-body-tabs" role="tablist" aria-label="Body & Table tools">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'table'}
          className={`pdf-studio-body-tab${tab === 'table' ? ' is-active' : ''}`}
          onClick={() => setTab('table')}
        >
          Edit table
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'sections'}
          className={`pdf-studio-body-tab${tab === 'sections' ? ' is-active' : ''}`}
          onClick={() => setTab('sections')}
        >
          Sections
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'colors'}
          className={`pdf-studio-body-tab${tab === 'colors' ? ' is-active' : ''}`}
          onClick={() => setTab('colors')}
        >
          Colors & fonts
        </button>
      </div>

      {tab === 'table' ? (
        legacyLocked ? (
          <p className="pdf-studio-hint">Table editing is locked for this legacy layout.</p>
        ) : (
          <TableColumnsEditor
            columns={globalLayout.body?.tableColumns || DEFAULT_TABLE_COLUMNS}
            onChange={(tableColumns) =>
              onPatchGlobal({
                body: {
                  ...(globalLayout.body || {}),
                  tableColumns
                }
              })
            }
          />
        )
      ) : null}

      {tab === 'sections' ? (
        legacyLocked ? (
          <p className="pdf-studio-hint">Section editing is locked for this legacy layout.</p>
        ) : (
          <BodySectionsEditor
            sections={globalLayout.body?.sections || DEFAULT_BODY_SECTIONS}
            sectionOrder={
              globalLayout.body?.sectionOrder ||
              (globalLayout.body?.sections || DEFAULT_BODY_SECTIONS).map((s) => s.id)
            }
            onChange={(sections, sectionOrder) =>
              onPatchGlobal({
                body: {
                  ...(globalLayout.body || {}),
                  sections,
                  sectionOrder
                }
              })
            }
          />
        )
      ) : null}

      {tab === 'colors' ? (
        <>
          <SliderField
            label="Body font size"
            scope="global"
            min={10}
            max={16}
            unit="px"
            disabled={legacyLocked}
            value={globalLayout.type.bodyPx}
            onChange={(bodyPx) => onPatchGlobal({ type: { ...globalLayout.type, bodyPx } })}
          />
          <SliderField
            label="Table font size"
            scope="global"
            min={10}
            max={15}
            unit="px"
            disabled={legacyLocked}
            value={globalLayout.type.tablePx}
            onChange={(tablePx) => onPatchGlobal({ type: { ...globalLayout.type, tablePx } })}
          />
          <ColorField
            label="Table heading background"
            value={globalLayout.colors.tableHeadBg}
            scope="global"
            disabled={legacyLocked}
            onChange={(tableHeadBg) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, tableHeadBg } })
            }
          />
          <ColorField
            label="Table heading text"
            value={globalLayout.colors.tableHeadText}
            scope="global"
            disabled={legacyLocked}
            onChange={(tableHeadText) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, tableHeadText } })
            }
          />
          <ColorField
            label="Alternating row color"
            value={globalLayout.body?.rowAltBg || '#f8fafc'}
            scope="global"
            disabled={legacyLocked}
            onChange={(rowAltBg) =>
              onPatchGlobal({ body: { ...(globalLayout.body || {}), rowAltBg } })
            }
          />
          <ColorField
            label="Muted text color"
            value={globalLayout.colors.bodyMutedColor}
            scope="global"
            disabled={legacyLocked}
            onChange={(bodyMutedColor) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, bodyMutedColor } })
            }
          />
          <ColorField
            label="Meta card background"
            value={globalLayout.colors.metaCardBg}
            scope="global"
            disabled={legacyLocked}
            onChange={(metaCardBg) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, metaCardBg } })
            }
          />
          <ColorField
            label="Meta card border"
            value={globalLayout.colors.metaCardBorder}
            scope="global"
            disabled={legacyLocked}
            onChange={(metaCardBorder) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, metaCardBorder } })
            }
          />
          <ColorField
            label="Meta card title color"
            value={globalLayout.colors.metaTitleColor}
            scope="global"
            disabled={legacyLocked}
            onChange={(metaTitleColor) =>
              onPatchGlobal({ colors: { ...globalLayout.colors, metaTitleColor } })
            }
          />
          <SliderField
            label="Meta card radius"
            scope="global"
            min={4}
            max={24}
            unit="px"
            disabled={legacyLocked}
            value={globalLayout.type.metaRadiusPx}
            onChange={(metaRadiusPx) =>
              onPatchGlobal({ type: { ...globalLayout.type, metaRadiusPx } })
            }
          />
        </>
      ) : null}
    </div>
  );
}

function reorderById<T extends { id: string }>(items: T[], fromId: string, toId: string): T[] {
  if (fromId === toId) return items;
  const from = items.findIndex((i) => i.id === fromId);
  const to = items.findIndex((i) => i.id === toId);
  if (from < 0 || to < 0) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function TableColumnsEditor(props: {
  columns: PdfTableColumn[];
  onChange: (columns: PdfTableColumn[]) => void;
}): React.JSX.Element {
  const { columns, onChange } = props;
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  function update(id: string, patch: Partial<PdfTableColumn>) {
    onChange(columns.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }

  return (
    <div className="pdf-studio-structure-editor">
      <p className="pdf-studio-subhead">Edit table columns</p>
      <p className="pdf-studio-hint">
        Drag the handle (⠿) to reorder. Add or delete columns for quote/invoice line-item tables.
      </p>
      <div className="pdf-studio-structure-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={columns.length >= PDF_LAYOUT_MAX_TABLE_COLUMNS}
          onClick={() =>
            onChange([
              ...columns,
              {
                id: crypto.randomUUID(),
                key: 'custom',
                label: `Column ${columns.length + 1}`,
                align: 'left',
                customText: ''
              }
            ])
          }
        >
          + Add column
        </button>
        <button type="button" className="btn-secondary" onClick={() => onChange([...DEFAULT_TABLE_COLUMNS])}>
          Reset defaults
        </button>
      </div>
      <ul className="pdf-studio-dnd-list">
        {columns.map((col) => (
          <li
            key={col.id}
            className={`pdf-studio-dnd-item${overId === col.id ? ' is-drop-target' : ''}${
              dragId === col.id ? ' is-dragging' : ''
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragId && dragId !== col.id) setOverId(col.id);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId) onChange(reorderById(columns, dragId, col.id));
              setDragId(null);
              setOverId(null);
            }}
          >
            <div className="pdf-studio-dnd-head">
              <button
                type="button"
                className="pdf-studio-drag-handle"
                draggable
                title="Drag to reorder"
                aria-label={`Drag column ${col.label}`}
                onDragStart={(e) => {
                  setDragId(col.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', col.id);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
              >
                ⠿
              </button>
              <span className="pdf-studio-dnd-title">{col.label || 'Untitled column'}</span>
              <button
                type="button"
                className="btn-secondary pdf-studio-dnd-delete"
                disabled={columns.length <= 1}
                onClick={() => onChange(columns.filter((c) => c.id !== col.id))}
              >
                Delete
              </button>
            </div>
            <TextField label="Header label" value={col.label} onChange={(label) => update(col.id, { label })} />
            <SelectField
              label="Data source"
              value={col.key}
              options={[
                { value: 'item', label: 'Item name' },
                { value: 'description', label: 'Description' },
                { value: 'quantity', label: 'Quantity' },
                { value: 'unitPrice', label: 'Unit price / Rate' },
                { value: 'amount', label: 'Amount (qty × rate)' },
                { value: 'custom', label: 'Custom / blank text' }
              ]}
              onChange={(key: PdfTableColumnKey) =>
                update(col.id, {
                  key,
                  showDescriptionUnderItem: key === 'item' ? true : undefined,
                  customText: key === 'custom' ? col.customText || '' : undefined
                })
              }
            />
            <SelectField
              label="Alignment"
              value={col.align || (col.key === 'item' || col.key === 'description' ? 'left' : 'right')}
              options={[
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
              ]}
              onChange={(align: 'left' | 'center' | 'right') => update(col.id, { align })}
            />
            {col.key === 'item' ? (
              <ToggleField
                label="Show description under item name"
                checked={col.showDescriptionUnderItem !== false}
                onChange={(showDescriptionUnderItem) => update(col.id, { showDescriptionUnderItem })}
              />
            ) : null}
            {col.key === 'custom' ? (
              <TextField
                label="Custom cell text"
                value={col.customText || ''}
                placeholder="Optional static text in every row"
                onChange={(customText) => update(col.id, { customText })}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

const BODY_SECTION_KIND_LABELS: Record<PdfBodySectionKind, string> = {
  title: 'Title row',
  meta: 'Bill To / details cards',
  itemsTable: 'Line-item table',
  totals: 'Totals',
  intro: 'Introduction / notes',
  signature: 'Signature & date',
  disclaimer: 'Disclaimer footer',
  compliance: 'Compliance box',
  footerStrip: 'Footer logo strip',
  customText: 'Custom text section'
};

function BodySectionsEditor(props: {
  sections: PdfBodySection[];
  sectionOrder: string[];
  onChange: (sections: PdfBodySection[], sectionOrder: string[]) => void;
}): React.JSX.Element {
  const { sections, sectionOrder, onChange } = props;
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const ordered = sectionOrder
    .map((id) => sections.find((s) => s.id === id))
    .filter((s): s is PdfBodySection => Boolean(s));
  for (const s of sections) {
    if (!ordered.some((o) => o.id === s.id)) ordered.push(s);
  }

  const hasSignature = sections.some((s) => s.kind === 'signature');

  function commit(nextSections: PdfBodySection[], nextOrder?: string[]) {
    onChange(nextSections, nextOrder || nextSections.map((s) => s.id));
  }

  function update(id: string, patch: Partial<PdfBodySection>) {
    commit(
      sections.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      ordered.map((s) => s.id)
    );
  }

  return (
    <div className="pdf-studio-structure-editor">
      <p className="pdf-studio-subhead">Body sections</p>
      <p className="pdf-studio-hint">
        Drag sections to place them where you want in the PDF body. Toggle visibility, or add a custom
        text block.
      </p>
      <div className="pdf-studio-structure-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={sections.length >= PDF_LAYOUT_MAX_BODY_SECTIONS}
          onClick={() => {
            const id = crypto.randomUUID();
            const next: PdfBodySection[] = [
              ...sections,
              {
                id,
                kind: 'customText',
                enabled: true,
                title: 'New section',
                text: ''
              }
            ];
            commit(next, [...ordered.map((s) => s.id), id]);
          }}
        >
          + Add section
        </button>
        {!hasSignature ? (
          <button
            type="button"
            className="btn-secondary"
            disabled={sections.length >= PDF_LAYOUT_MAX_BODY_SECTIONS}
            onClick={() => {
              const id = 'sec-signature';
              const next: PdfBodySection[] = [
                ...sections,
                {
                  id,
                  kind: 'signature',
                  enabled: true,
                  title: 'Authorization'
                }
              ];
              commit(next, [...ordered.map((s) => s.id), id]);
            }}
          >
            + Add signature &amp; date
          </button>
        ) : null}
        <button
          type="button"
          className="btn-secondary"
          onClick={() => commit([...DEFAULT_BODY_SECTIONS], DEFAULT_BODY_SECTIONS.map((s) => s.id))}
        >
          Reset defaults
        </button>
      </div>
      <ul className="pdf-studio-dnd-list">
        {ordered.map((section) => (
          <li
            key={section.id}
            className={`pdf-studio-dnd-item${overId === section.id ? ' is-drop-target' : ''}${
              dragId === section.id ? ' is-dragging' : ''
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragId && dragId !== section.id) setOverId(section.id);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (!dragId) return;
              const nextOrdered = reorderById(ordered, dragId, section.id);
              commit(sections, nextOrdered.map((s) => s.id));
              setDragId(null);
              setOverId(null);
            }}
          >
            <div className="pdf-studio-dnd-head">
              <button
                type="button"
                className="pdf-studio-drag-handle"
                draggable
                title="Drag to place this section"
                aria-label={`Drag section ${BODY_SECTION_KIND_LABELS[section.kind]}`}
                onDragStart={(e) => {
                  setDragId(section.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', section.id);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
              >
                ⠿
              </button>
              <span className="pdf-studio-dnd-title">
                {section.title?.trim() || BODY_SECTION_KIND_LABELS[section.kind] || section.kind}
              </span>
              {section.kind === 'customText' || section.kind === 'signature' ? (
                <button
                  type="button"
                  className="btn-secondary pdf-studio-dnd-delete"
                  onClick={() => {
                    const next = sections.filter((s) => s.id !== section.id);
                    commit(
                      next,
                      ordered.map((s) => s.id).filter((id) => id !== section.id)
                    );
                  }}
                >
                  Delete
                </button>
              ) : null}
            </div>
            <ToggleField
              label="Include in PDF"
              checked={section.enabled !== false}
              onChange={(enabled) => update(section.id, { enabled })}
            />
            {section.kind === 'customText' ||
            section.kind === 'intro' ||
            section.kind === 'signature' ? (
              <>
                <TextField
                  label="Section title"
                  value={section.title || ''}
                  placeholder={
                    section.kind === 'intro'
                      ? 'e.g. Introduction, Scope, Notes…'
                      : section.kind === 'signature'
                        ? 'e.g. Authorization, Acceptance…'
                        : 'e.g. Terms & Conditions'
                  }
                  onChange={(title) => update(section.id, { title })}
                />
                {section.kind === 'customText' ? (
                  <TextAreaField
                    label="Section text"
                    rows={3}
                    value={section.text || ''}
                    placeholder="Write the content that should appear in this section…"
                    onChange={(text) => update(section.id, { text })}
                  />
                ) : section.kind === 'signature' ? (
                  <p className="pdf-studio-hint" style={{ marginTop: 4 }}>
                    Adds blank Signature, Date, and Print name lines for the customer to sign. The
                    title above is the heading in the PDF.
                  </p>
                ) : (
                  <p className="pdf-studio-hint" style={{ marginTop: 4 }}>
                    Quote text for this section is entered on each quote (not here). The title above is
                    what appears as the heading in the PDF.
                  </p>
                )}
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ExtraLogosEditor(props: {
  layout: PdfCustomLayout;
  onChange: (extras: PdfCustomLayout['extras'], extraOrder: string[]) => void;
}): React.JSX.Element {
  const { layout, onChange } = props;
  return (
    <div className="pdf-studio-extras">
      <p className="pdf-studio-subhead">Extra header logos</p>
      <p className="pdf-studio-hint">Max {PDF_LAYOUT_MAX_EXTRAS}. Use sliders to position each.</p>
      <input
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          void (async () => {
            const next = [...layout.extras];
            for (const f of files) {
              if (next.length >= PDF_LAYOUT_MAX_EXTRAS) break;
              const dataUrl = await fileToDataUrl(f);
              const id = crypto.randomUUID();
              next.push({ id, dataUrl, xFrac: 0.72, yFrac: 0.5, slotWidthFrac: 0.14 });
            }
            onChange(next, next.map((x) => x.id));
          })();
        }}
      />
      <ul className="pdf-studio-extras-list">
        {layout.extras.map((ex, idx) => (
          <li key={ex.id} className="pdf-studio-extra-item">
            <div className="pdf-studio-extra-head">
              {ex.dataUrl ? <img className="pdf-studio-preview-thumb" src={ex.dataUrl} alt="" /> : null}
              <span>Logo {idx + 1}</span>
            </div>
            <SliderField
              label="Width"
              min={8}
              max={42}
              unit="%"
              value={Math.round(ex.slotWidthFrac * 100)}
              onChange={(pct) => {
                const next = layout.extras.map((x) =>
                  x.id === ex.id
                    ? { ...x, slotWidthFrac: Math.min(0.42, Math.max(0.06, pct / 100)) }
                    : x
                );
                onChange(next, layout.extraOrder || next.map((x) => x.id));
              }}
            />
            <SliderField
              label="X position"
              min={0}
              max={100}
              unit="%"
              value={Math.round(ex.xFrac * 100)}
              onChange={(pct) => {
                const next = layout.extras.map((x) =>
                  x.id === ex.id ? { ...x, xFrac: Math.min(1, Math.max(0, pct / 100)) } : x
                );
                onChange(next, layout.extraOrder || next.map((x) => x.id));
              }}
            />
            <SliderField
              label="Y position"
              min={0}
              max={100}
              unit="%"
              value={Math.round(ex.yFrac * 100)}
              onChange={(pct) => {
                const next = layout.extras.map((x) =>
                  x.id === ex.id ? { ...x, yFrac: Math.min(1, Math.max(0, pct / 100)) } : x
                );
                onChange(next, layout.extraOrder || next.map((x) => x.id));
              }}
            />
            <div className="pdf-studio-row">
              <button
                type="button"
                className="btn-secondary"
                disabled={idx === 0}
                onClick={() => {
                  const order = [...(layout.extraOrder || layout.extras.map((x) => x.id))];
                  const i = order.indexOf(ex.id);
                  if (i > 0) {
                    [order[i - 1], order[i]] = [order[i], order[i - 1]];
                    const sorted = order
                      .map((id) => layout.extras.find((x) => x.id === id)!)
                      .filter(Boolean);
                    onChange(sorted, order);
                  }
                }}
              >
                Up
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={idx === layout.extras.length - 1}
                onClick={() => {
                  const order = [...(layout.extraOrder || layout.extras.map((x) => x.id))];
                  const i = order.indexOf(ex.id);
                  if (i >= 0 && i < order.length - 1) {
                    [order[i], order[i + 1]] = [order[i + 1], order[i]];
                    const sorted = order
                      .map((id) => layout.extras.find((x) => x.id === id)!)
                      .filter(Boolean);
                    onChange(sorted, order);
                  }
                }}
              >
                Down
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  const next = layout.extras.filter((x) => x.id !== ex.id);
                  onChange(next, next.map((x) => x.id));
                }}
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
